/**
 * Mysa Product Card Component
 *
 * Custom element that applies utility partner discounts to product cards.
 * Uses utility-price-handler for discount calculation and section reloading.
 *
 * Discount calculation:
 * - Sitewide/launch/custom-price: Calculated server-side by Liquid
 * - Utility discounts: Calculated client-side by utility-price-handler
 *
 * @module MysaProductCard
 */

import {
  findSectionId,
  formatPrice,
  applyUtilityDiscountWhenReady,
  setupUtilityListeners,
  syncPriceSaleSticker,
} from "@theme/utility-price-handler";

/**
 * Product card custom element that handles utility discount display
 */
class MysaProductCard extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;

    // Read prices from inner price wrapper (set by mysa-price-display snippet)
    // Falls back to custom element data attributes for backwards compatibility
    // Uses isNaN instead of || so that 0 (no compare-at price) is preserved
    const priceWrapper = this.querySelector(".product-card-price-wrapper");
    if (priceWrapper) {
      const parsedOriginal = parseInt(priceWrapper.dataset.originalPrice, 10);
      const parsedCalculated = parseInt(
        priceWrapper.dataset.calculatedPrice,
        10,
      );
      this.originalPrice = !isNaN(parsedOriginal)
        ? parsedOriginal
        : parseInt(this.dataset.originalPrice, 10);
      this.discountedPrice = !isNaN(parsedCalculated)
        ? parsedCalculated
        : parseInt(this.dataset.discountedPrice, 10);
    } else {
      this.originalPrice = parseInt(this.dataset.originalPrice, 10);
      this.discountedPrice = parseInt(this.dataset.discountedPrice, 10);
    }

    // Always find section ID and setup utility listeners.
    // Section morphing must happen regardless of current price data —
    // the morph re-renders the card with correct server-side Liquid output.
    this.sectionId = findSectionId(this);
    this.cleanupUtilityListeners = setupUtilityListeners({
      sectionId: this.sectionId,
      onSectionReloaded: () => this.#applyUtilityDiscount(),
    });

    if (!this.handle || isNaN(this.discountedPrice)) return;

    this.#applyUtilityDiscount();
  }

  disconnectedCallback() {
    this.cleanupUtilityListeners?.();
  }

  /**
   * Apply utility discount on top of server-rendered price
   * Skips if Liquid already applied the utility discount (data-utility-applied="true")
   */
  #applyUtilityDiscount() {
    // Re-read prices from DOM in case section was reloaded
    const priceWrapper = this.querySelector(".product-card-price-wrapper");
    if (priceWrapper) {
      const calculatedPrice = parseInt(
        priceWrapper.dataset.calculatedPrice,
        10,
      );
      const originalPrice = parseInt(priceWrapper.dataset.originalPrice, 10);
      if (!isNaN(calculatedPrice)) {
        this.discountedPrice = calculatedPrice;
      }
      if (!isNaN(originalPrice)) {
        this.originalPrice = originalPrice;
      }

      // If Liquid already applied utility discount, skip JS application
      // The section will reload with correct prices when utility changes
      if (priceWrapper.dataset.utilityApplied === "true") {
        return;
      }
    }

    applyUtilityDiscountWhenReady(
      this.handle,
      this.discountedPrice,
      (discountAmount, priceFloor) => {
        this.#updatePriceDisplay(discountAmount, priceFloor);
      },
    );
  }

  /**
   * Update the price display with utility discount
   * @param {number} discountAmount - Utility discount amount in cents
   * @param {number | null} priceFloor - Price floor in cents (null if no floor)
   */
  #updatePriceDisplay(discountAmount, priceFloor) {
    const priceWrapper = this.querySelector(".product-card-price-wrapper");
    if (!priceWrapper) return;

    const priceEl = priceWrapper.querySelector(".product-card-price");
    if (!priceEl) return;

    // discountedPrice already has sitewide/launch discounts applied
    // Now apply the utility discount on top of that
    let finalPrice = this.discountedPrice - discountAmount;

    // Enforce price floor if exists (prevents going below minimum price)
    // This handles race conditions where Liquid applied sitewide before
    // knowing about the utility partner's price floor
    if (priceFloor !== null && finalPrice < priceFloor) {
      finalPrice = priceFloor;
    }

    // Update main price
    priceEl.textContent = formatPrice(finalPrice);

    // Add compare price if not exists - show ORIGINAL price (before all discounts)
    let comparePrice = priceWrapper.querySelector(
      ".product-card-price.as-compare",
    );
    if (!comparePrice) {
      comparePrice = document.createElement("span");
      comparePrice.className = "product-card-price as-compare";
      priceEl.after(comparePrice);
    }
    comparePrice.textContent = formatPrice(this.originalPrice);

    syncPriceSaleSticker(this, {
      originalPrice: this.originalPrice,
      finalPrice,
      hasDiscount: true,
      utilityAdjusted: true,
    });
  }
}

// Register custom element
if (!customElements.get("mysa-product-card")) {
  customElements.define("mysa-product-card", MysaProductCard);
}
